import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Copy, Eye, EyeOff } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

/**
 * A value someone has to paste elsewhere — a URL, a token, a manifest — with
 * one button that copies it and says so. `secret` masks it until revealed.
 */
export function CopyField({
  label,
  value,
  secret,
  multiline,
  prose,
  className,
  testId,
}: {
  label?: React.ReactNode;
  value: string;
  secret?: boolean;
  multiline?: boolean;
  /** Text people read (an email draft), not a token: the body font, wrapped between words. */
  prose?: boolean;
  className?: string;
  testId?: string;
}) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [shown, setShown] = useState(!secret);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      // Clipboard refused (insecure context): the value is still selectable.
    }
  };

  const display = shown ? value : '•'.repeat(Math.min(32, Math.max(12, value.length)));

  return (
    <div className={cn('flex flex-col gap-1.5', className)} data-testid={testId}>
      {label && <span className="text-xs font-medium text-muted-foreground">{label}</span>}
      <div className="flex items-start gap-1 rounded-lg border bg-muted/40 p-1 ps-3">
        {multiline ? (
          <pre className={cn('max-h-56 min-w-0 flex-1 overflow-auto py-1.5 leading-relaxed whitespace-pre-wrap', prose ? 'font-sans text-sm break-words' : 'font-mono text-[11px] break-all')}>{display}</pre>
        ) : (
          <code className="min-w-0 flex-1 truncate py-1.5 font-mono text-xs" title={shown ? value : undefined}>{display}</code>
        )}
        {secret && (
          <Button variant="ghost" size="icon-sm" onClick={() => setShown((s) => !s)} aria-label={t(shown ? 'ARDI.COMMON.HIDE' : 'ARDI.COMMON.SHOW') as string}>
            {shown ? <EyeOff /> : <Eye />}
          </Button>
        )}
        <Button variant="ghost" size="icon-sm" onClick={copy} aria-label={t(copied ? 'ARDI.COMMON.COPIED' : 'ARDI.COMMON.COPY') as string}>
          {copied ? <Check /> : <Copy />}
        </Button>
      </div>
    </div>
  );
}
